import { z } from "zod";

import { isValidLicensePlate } from "../case";
import { requiredCpfSchema } from "../client";

/**
 * Values are stored verbatim on tasks — add, never rename or remove.
 */
export const DvcSearchTypeSchema = z.enum([
  "plate_current_owner",
  "plate_owner_history",
  "cpf_lookup",
]);
export type DvcSearchType = z.infer<typeof DvcSearchTypeSchema>;

const DVC_SEARCH_DATA_MAX = 120;

const PLATE_SEARCH_TYPES: readonly DvcSearchType[] = ["plate_current_owner", "plate_owner_history"];

export const DvcSearchDetailsSchema = z
  .object({
    type: DvcSearchTypeSchema.describe("The kind of DVC search being requested").meta({
      visibleTo: "*",
    }),
    dataToSearch: z
      .string()
      .min(1)
      .max(DVC_SEARCH_DATA_MAX)
      .describe("The plate, CPF, or other value to run the search on")
      .meta({ visibleTo: "*" }),
  })
  .refine(
    ({ type, dataToSearch }) =>
      !PLATE_SEARCH_TYPES.includes(type) || isValidLicensePlate(dataToSearch),
    { message: "case_validation_license_plate_invalid", path: ["dataToSearch"] },
  )
  .refine(
    ({ type, dataToSearch }) =>
      type !== "cpf_lookup" || requiredCpfSchema.safeParse(dataToSearch).success,
    { message: "clients_validation_cpf", path: ["dataToSearch"] },
  );

export type DvcSearchDetails = z.infer<typeof DvcSearchDetailsSchema>;

export const dvcSearchDetailsField = DvcSearchDetailsSchema.optional().describe(
  "DVC search parameters; only accepted by task types that collect them",
);

const CURRENT_OWNER_LOOKUP_MAX_AGE_MONTHS = 6;

/**
 * A current-owner lookup answers who holds the plate today, so it only speaks
 * for a recent incident: past six months the plate may already have changed
 * hands and the owner history is the search that still answers.
 */
export const isDvcSearchTypeAvailable = (
  dvcSearchType: DvcSearchType,
  incidentDate: string | null | undefined,
  today: Date = new Date(),
): boolean =>
  dvcSearchType !== "plate_current_owner" ||
  !incidentDate ||
  incidentDate.slice(0, 10) >= monthsBefore(today, CURRENT_OWNER_LOOKUP_MAX_AGE_MONTHS);

/** Two requests ask the same thing when the lookup and its value match, however the value was typed. */
export const isSameDvcSearch = (a: DvcSearchDetails, b: DvcSearchDetails): boolean =>
  a.type === b.type && searchedValueKey(a.dataToSearch) === searchedValueKey(b.dataToSearch);

const searchedValueKey = (dataToSearch: string): string =>
  dataToSearch.replaceAll(/[^0-9a-z]/gi, "").toUpperCase();

const monthsBefore = (date: Date, months: number): string => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() - months;
  const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

  return new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), lastDayOfMonth)))
    .toISOString()
    .slice(0, 10);
};
